import React, { useEffect, useRef } from "react";
import * as echarts from 'echarts';
import DashboardMainContainer from "../common/Containers/DashboardMainContainer";
import { AdminPanelTitle, AdminMetric } from '../../sections/AdminDashboard/shared/adminUx';
import { AdminWidgetHeader } from './AdminWidgetChrome';
import { dashboardChartTooltipPieFormatter } from './dashboardChartTooltip';

const PostQuotaWidget = () => {
  const postQuotaChartRef = useRef(null);

  // Post quota data
  const postQuotaData = {
    standard: { used: 2, total: 3 },
    featured: { used: 8, total: 10 },
    resetDate: "Sep 1, 2025"
  };

  // Initialize chart
  useEffect(() => {
    if (postQuotaChartRef.current) {
      const chart = echarts.init(postQuotaChartRef.current);
      
      // Calculate percentages
      const standardPercentage = (postQuotaData.standard.used / postQuotaData.standard.total) * 100;
      const featuredPercentage = (postQuotaData.featured.used / postQuotaData.featured.total) * 100;
      const standardRemainingPercentage = 100 - standardPercentage;
      const featuredRemainingPercentage = 100 - featuredPercentage;
      
      const option = {
        backgroundColor: 'transparent',
        tooltip: { 
          trigger: 'item',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          textStyle: { color: '#374151' },
          renderMode: 'html',
          formatter: (params) => {
            if (!params?.data) return '';
            const data = params.data;
            const isUsed = data.name.includes('Used');
            const postType = data.name.includes('Standard') ? 'Standard' : 'Featured';
            const count = isUsed
              ? postType === 'Standard'
                ? postQuotaData.standard.used
                : postQuotaData.featured.used
              : postType === 'Standard'
                ? postQuotaData.standard.total - postQuotaData.standard.used
                : postQuotaData.featured.total - postQuotaData.featured.used;
            const total =
              postType === 'Standard' ? postQuotaData.standard.total : postQuotaData.featured.total;
            return dashboardChartTooltipPieFormatter(params, {
              title: `${postType} Posts`,
              label: isUsed ? 'Used' : 'Remaining',
              value: `${count}/${total}`,
            });
          },
        },
        series: [
          // Outer circle - Featured Posts
          {
            type: 'pie',
            radius: ['80%', '100%'],
            center: ['50%', '50%'],
            startAngle: 90,
            itemStyle: {
              borderRadius: 0
            },
            data: [
              { 
                value: featuredPercentage, 
                name: 'Featured Used', 
                itemStyle: { 
                  color: '#03A62C' // freshgreen from Tailwind config
                } 
              },
              { 
                value: featuredRemainingPercentage, 
                name: 'Featured Remaining', 
                itemStyle: { color: '#b7ead9' } // Light gray
              }
            ],
            label: {
              show: false
            },
            labelLine: {
              show: false
            },
            emphasis: {
              scale: false
            }
          },
          // Inner circle - Standard Posts
          {
            type: 'pie',
            radius: ['50%', '70%'],
            center: ['50%', '50%'],
            startAngle: 90,
            itemStyle: {
              borderRadius: 0
            },
            data: [
              { 
                value: standardPercentage, 
                name: 'Standard Used', 
                itemStyle: { 
                  color: '#2E00FF' // primaryblue from Tailwind config
                } 
              },
              { 
                value: standardRemainingPercentage, 
                name: 'Standard Remaining', 
                itemStyle: { color: '#d5ccff' } // Light gray
              }
            ],
            label: {
              show: false
            },
            labelLine: {
              show: false
            },
            emphasis: {
              scale: false
            }
          }
        ]
      };
      chart.setOption(option);
      
      // Handle resize
      const handleResize = () => {
        chart.resize();
      };
      window.addEventListener('resize', handleResize);
      
      // Cleanup
      return () => {
        window.removeEventListener('resize', handleResize);
        chart.dispose();
      };
    }
  }, []);

  return (
    <DashboardMainContainer className="masonry-item w-[270px] h-[270px] rounded-lg flex flex-col">
      <AdminWidgetHeader title="Post Quota" />
      <div className="flex-1 p-4 flex flex-col items-center justify-center">
        {/* Donut Chart */}
        <div className="relative mb-6">
          <div ref={postQuotaChartRef} className="w-32 h-32" />
        </div>
        
        {/* Quota Breakdown */}
        <div className="w-full space-y-1 mb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#2E00FF'}}></div>
              <AdminPanelTitle className="text-offblack">Post</AdminPanelTitle>
            </div>
            <AdminMetric size="small" className="text-offblack tabular-nums">
              {postQuotaData.standard.used}/{postQuotaData.standard.total}
            </AdminMetric>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#03A62C'}}></div>
              <AdminPanelTitle className="text-offblack">Featured Post</AdminPanelTitle>
            </div>
            <AdminMetric size="small" className="text-offblack tabular-nums">
              {postQuotaData.featured.used}/{postQuotaData.featured.total}
            </AdminMetric>
          </div>
        </div>
      </div>
    </DashboardMainContainer>
  );
};

export default PostQuotaWidget;
